import Typography from '@mui/material/Typography';
import { endOfYear, startOfDay } from 'date-fns';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'shared/assets/icons';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { CustomFormSelect } from 'shared/components/ui/custom-form-select';
import { Icon } from 'shared/components/ui/icon';
import { useTrackEvents } from 'shared/hooks/use-track-events';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { ActionEvents, PageEvents } from 'shared/services/tracking';
import { ID } from 'shared/types/id';
import {
  OnboardingGoal,
  OnboardingLocalizedLifeArea,
} from 'shared/types/onboarding-answers';
import { v4 as uuidv4 } from 'uuid';

import { OnboardingHeader } from '../onboarding-header';
import { OnboardingSideImage } from '../onboarding-side-image';
import { ProgressBar } from '../progress-bar';
import * as Styled from './goals.style';

const GOALS_IMAGE_URL = '/images/onboarding/goal-roadmap.png';

export type GoalsProps = {
  nextStep: () => void;
  prevStep: () => void;
  currentStep: number;
  previousStep: number;
  totalSteps: number;
  availableLifeAreas: OnboardingLocalizedLifeArea[];
  onSubmit: (goals: OnboardingGoal[]) => void;
};

export const Goals: React.FC<GoalsProps> = ({
  nextStep,
  prevStep,
  currentStep,
  previousStep,
  totalSteps,
  availableLifeAreas,
  onSubmit,
}) => {
  useTrackPageVisitEvent(PageEvents.OnboardingGoals, undefined);
  const track = useTrackEvents();
  const { t } = useTranslation();
  const firstInputRef = useRef<HTMLInputElement | null>(null);
  const [goals, setGoals] = useState(['', '']);
  const [selectedLifeAreas, setSelectedLifeAreas] = useState<
    (ID | undefined)[]
  >([undefined, undefined]);

  const suggestions = useMemo(
    () => [
      t('forms.onboarding.goals.suggestions.1'),
      t('forms.onboarding.goals.suggestions.2'),
      t('forms.onboarding.goals.suggestions.3'),
      t('forms.onboarding.goals.suggestions.4'),
      t('forms.onboarding.goals.suggestions.5'),
      t('forms.onboarding.goals.suggestions.6'),
      t('forms.onboarding.goals.suggestions.7'),
    ],
    [t],
  );

  const unusedSuggestions = useMemo(
    () => suggestions.filter((s) => !goals.includes(s)),
    [suggestions, goals],
  );

  useEffect(() => {
    const isTouchDevice = 'ontouchstart' in window;
    if (firstInputRef.current && !isTouchDevice) {
      firstInputRef.current.focus();
    }
  }, []);

  const lifeAreaOptions = useMemo(
    () =>
      availableLifeAreas.map(({ id, name }) => ({
        value: id,
        label: name,
      })),
    [availableLifeAreas],
  );

  const handleGoalChange = (index: number, value: string) => {
    const newGoals = [...goals];
    newGoals[index] = value;
    setGoals(newGoals);
  };

  const handleLifeAreaChange = (index: number, value?: ID) => {
    const newSelected = [...selectedLifeAreas];
    newSelected[index] = value;
    setSelectedLifeAreas(newSelected);
  };

  const handleSuggestionClick = (goalText: string) => {
    const firstEmptyIndex = goals.findIndex((g) => g === '');
    if (firstEmptyIndex !== -1) {
      track(ActionEvents.OnboardingGoalsSuggestionClicked, {
        example: goalText,
      });
      handleGoalChange(firstEmptyIndex, goalText);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(
      goals
        .map((name, i) => ({
          id: uuidv4(),
          name: name.trim(),
          lifeAreaId: selectedLifeAreas[i],
          deadline: endOfYear(new Date()),
          startDate: startOfDay(new Date()),
        }))
        .filter(({ name }) => !!name),
    );
    nextStep();
  };

  return (
    <Styled.OnboardingContainer>
      <Styled.LeftPanel>
        <OnboardingHeader onBack={prevStep} />
        <Styled.LeftPanelContent>
          <ProgressBar
            current={currentStep}
            previous={previousStep}
            total={totalSteps}
          />

          <Styled.Form id="goals-form" onSubmit={handleSubmit}>
            <Typography variant="h4" component="h1">
              {t('forms.onboarding.goals.title')}
            </Typography>

            <Styled.GoalsContainer>
              {goals.map((goal, index) => {
                const selectedAreaId = selectedLifeAreas[index];

                return (
                  <Styled.GoalRow key={index}>
                    <Styled.Input
                      ref={!index ? firstInputRef : null}
                      placeholder={t('forms.goal.fields.name.placeholder')}
                      value={goal}
                      onChange={(e) => handleGoalChange(index, e.target.value)}
                    />

                    <CustomFormSelect
                      options={lifeAreaOptions}
                      value={selectedAreaId}
                      onChange={(id) => handleLifeAreaChange(index, id)}
                      placeholder={t(
                        'forms.onboarding.goals.life-area-select.placeholder',
                      )}
                      noBorders
                      isSmall
                      canClear
                      inheritColor
                    />
                  </Styled.GoalRow>
                );
              })}
            </Styled.GoalsContainer>

            <Styled.ChipsRow>
              {unusedSuggestions.map((goal) => (
                <Styled.Chip
                  key={goal}
                  type="button"
                  onClick={() => handleSuggestionClick(goal)}
                >
                  <Icon icon={Plus} />
                  <Styled.ChipLabel>{goal}</Styled.ChipLabel>
                </Styled.Chip>
              ))}
            </Styled.ChipsRow>
          </Styled.Form>

          <Styled.FooterArea>
            <Button
              type="submit"
              form="goals-form"
              size={Sizes.Medium}
              fullWidth
              alignCenter
            >
              {t('forms.onboarding.continue.label')}
            </Button>
            <Button
              type="button"
              onClick={nextStep}
              variant={Variants.Text}
              size={Sizes.Medium}
              color={Colors.Inherit}
              fullWidth
              alignCenter
            >
              {t('forms.onboarding.skip.label')}
            </Button>
          </Styled.FooterArea>
        </Styled.LeftPanelContent>
      </Styled.LeftPanel>
      <Styled.RightPanelWrapper>
        <OnboardingSideImage imageUrl={GOALS_IMAGE_URL} maxImageWidth={656} />
      </Styled.RightPanelWrapper>
    </Styled.OnboardingContainer>
  );
};
